/**
 * ShellLayout — risk.mypcb.com frame (redesign 0.24.0, Bill 2026-11-03): the shared
 * @retail-ops/panel-shell also used by admin.mypcb.com and sysops.mypcb.com.
 *
 * Sidebar: logo, "Risk Management" and the version, page search (Ctrl K), Home and the sections
 * from shell/riskNav.ts (Reviews, Settings, Disclosures, Records), role-gated as App.tsx gates the
 * routes. No platform switcher (risk has no platforms). Top bar: breadcrumbs, the guided tour,
 * per-page help and the account menu (About, Sign out). Under 900px the sidebar is a drawer.
 */

import { useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { PanelShell, pagesToSearchItems, tasksToSearchItems, type ShellLinkComponent } from '@retail-ops/panel-shell';
import { TourStartButton } from '@retail-ops/guided-tour';
import { PanelSupportReport } from './PanelSupportReport';
import { PanelGuidedTours } from '@/tours/PanelGuidedTours';
import { useAuth } from '@/contexts/AuthContext';
import { APP_VERSION } from '@/constants/version';
import { SHOW_UNPROVEN } from '@/App';
import { COMPLIANCE_REVIEWS_PATH, riskNav, riskTasksFor } from '@/shell/riskNav';
import { useReviewSummary } from '@/modules/ComplianceReviews/useReviewSummary';

export const ShellLink = Link as unknown as ShellLinkComponent;

export function useRiskNav() {
  const { user } = useAuth();
  const roles = useMemo(() => user?.roles ?? [], [user]);
  // Compliance reviews (Bill 2026-11-04): the sidebar badge counts the items waiting for a compliance review.
  const { summary } = useReviewSummary();
  const waiting = summary?.counts.needsReview;
  const nav = useMemo(() => riskNav(roles, SHOW_UNPROVEN, { [COMPLIANCE_REVIEWS_PATH]: waiting }), [roles, waiting]);
  const tasks = useMemo(() => riskTasksFor(nav), [nav]);
  const searchItems = useMemo(() => [...pagesToSearchItems(nav), ...tasksToSearchItems(tasks)], [nav, tasks]);
  return { nav, tasks, searchItems };
}

export function ShellLayout(): JSX.Element {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { nav, searchItems } = useRiskNav();

  return (
    <PanelGuidedTours>
      <PanelShell
        appName="Risk Management"
        version={APP_VERSION}
        pathname={pathname}
        Link={ShellLink}
        navigate={(to) => void navigate(to)}
        nav={nav}
        searchItems={searchItems}
        user={user ? { displayName: user.displayName || user.upn, detail: user.upn } : null}
        onSignOut={() => void logout()}
        aboutPath="/about"
        crumbs={pathname === '/about' ? [{ label: 'Risk Management', path: '/' }, { label: 'About this app' }] : undefined}
        topbarExtras={<TourStartButton className="btn btn--ghost btn--sm btn--invert" />}
        changesNeedReason
        homeTour="panel-home"
        userTour="panel-user"
      >
        <Outlet />
      </PanelShell>
      <PanelSupportReport />
    </PanelGuidedTours>
  );
}
